import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "rwana_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const didMount = useRef(false);

  // hydrate from localStorage (runs first, before any write)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    // Skip the very first mount-run so we don't overwrite persisted cart with []
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  const add = useCallback((product, variant = "", qty = 1) => {
    setItems((curr) => {
      const idx = curr.findIndex((i) => i.slug === product.slug && i.variant === variant);
      if (idx >= 0) {
        const next = [...curr];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [...curr, { slug: product.slug, name: product.name, price: product.price, image: product.image, variant, qty }];
    });
  }, []);

  const remove = useCallback((slug, variant) => {
    setItems((curr) => curr.filter((i) => !(i.slug === slug && i.variant === variant)));
  }, []);

  const updateQty = useCallback((slug, variant, qty) => {
    setItems((curr) => curr.map((i) => (i.slug === slug && i.variant === variant ? { ...i, qty: Math.max(1, qty) } : i)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totalItems = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartContext.Provider value={{ items, add, remove, updateQty, clear, totalItems }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
